import React, { useState } from 'react';
import { Modal, Button, Form, Row, Col, Container } from 'react-bootstrap';

interface BadgeModalProps {
  show: boolean;
  onHide: () => void;
  assessmentId?: string | null;
  reportId?: string | null;
}

const SupplierBadgeEmbedModal: React.FC<BadgeModalProps> = ({
  show,
  onHide,
  assessmentId,
  reportId,
}) => {
  // State to track which badge style is selected (0 to 7)
  const [selectedStyle, setSelectedStyle] = useState<number>(0);
  const [copied, setCopied] = useState(false);

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL!;

  // Helper to generate URL based on index
  const getBadgeUrl = (index: number) => {
    const styleParam = index === 0 ? 'badge' : `badge${index}`;
    return `${supabaseUrl}/functions/v1/get-supplier-report-badge?id=${assessmentId}&style=${styleParam}`;
  };

  const currentBadgeUrl = getBadgeUrl(selectedStyle);
  const verifyUrl = `${window.location.origin}/public/report/${reportId}`;

  const embedCode = `<a href="${verifyUrl}" target="_blank" rel="noopener noreferrer">
  <img src="${currentBadgeUrl}" alt="ESG Compliance Score" style="border:none;" />
</a>`;

  const handleCopy = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Modal show={show} onHide={onHide} centered size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Embed Report Badge</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <Container fluid>
          <Row>
            {/* LEFT SIDE: Selection Panel */}
            <Col
              md={5}
              className="border-end p-4 bg-light"
              style={{ maxHeight: '70vh', overflowY: 'auto' }}
            >
              <h6 className="fw-bold mb-3">1. Select Badge Style</h6>
              <div className="d-flex flex-column gap-3">
                {[...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    onClick={() => setSelectedStyle(i)}
                    className={`p-3 rounded border text-center cursor-pointer transition-all ${
                      selectedStyle === i
                        ? 'border-primary bg-white shadow-sm'
                        : 'bg-transparent border-secondary-subtle opacity-75'
                    }`}
                    style={{ cursor: 'pointer', transition: '0.2s' }}
                  >
                    <img
                      src={getBadgeUrl(i)}
                      alt={`Style ${i}`}
                      className="img-fluid"
                      style={{ pointerEvents: 'none' }}
                    />
                  </div>
                ))}
              </div>
            </Col>

            {/* RIGHT SIDE: Code & Preview Panel */}
            <Col
              md={7}
              className="p-4 d-flex flex-column justify-content-center"
            >
              <h6 className="fw-bold mb-3">2. Preview & Copy Code</h6>

              <div className="text-center mb-4 p-4 border rounded bg-primary-subtle">
                <label className="text-xs text-uppercase fw-bold text-muted d-block mb-3">
                  Live Preview
                </label>
                <img
                  src={currentBadgeUrl}
                  alt="Selected Badge"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '150px',
                    border: 'none',
                  }}
                />
              </div>

              <Form.Group>
                <Form.Label className="small fw-bold">
                  HTML Embed Code
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={6}
                  readOnly
                  value={embedCode}
                  className="font-monospace bg-dark text-info mb-3"
                  style={{ fontSize: '0.8rem' }}
                />
                <Button
                  variant={copied ? 'success' : 'primary'}
                  className="w-100 py-2 d-flex align-items-center justify-content-center"
                  onClick={handleCopy}
                >
                  <i
                    className={`bi bi-${
                      copied ? 'check-lg' : 'clipboard'
                    } me-2`}
                  ></i>
                  {copied ? 'Code Copied!' : 'Copy Embed Code'}
                </Button>
              </Form.Group>

              <p className="text-muted mt-3 small">
                Paste this code into your website's HTML where you want the
                badge to appear.
              </p>
            </Col>
          </Row>
        </Container>
      </Modal.Body>
    </Modal>
  );
};

export default SupplierBadgeEmbedModal;
